import ApplicationNavbar from "../../components/ApplicationNavbar";
import SwaggerUI from "swagger-ui-react";
import styled from "@emotion/styled";
import "swagger-ui-react/swagger-ui.css"; // Make sure you import Swagger's default CSS first

const PRIMARY_COLOR = "#3b82f6"; // blue-500
const SUCCESS_COLOR = "#10b981"; // green-500
const WARNING_COLOR = "#f59e0b"; // amber-500
const DANGER_COLOR  = "#ef4444"; // red-500
const FONT_FAMILY   = "'Inter', 'Open Sans', sans-serif";

const SwaggerContainer = styled.div`
  /* Layout & container styling */
  flex: 1;
  overflow: auto;
  padding: 2rem;
  background-color: #111827; /* gray-900 */
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  /* Base typography */
  font-family: ${FONT_FAMILY};
  font-size: 16px;
  line-height: 1.5;
  color: #f9fafb; /* gray-50 */

  /* Make sure we override swagger-ui classes from the parent scope */
  .swagger-ui {
    /* Tweak the global background if you like */
    background-color: #111827; /* gray-900 */
    color: #f9fafb; /* gray-50 */
    padding-bottom: 2rem; /* some extra spacing at bottom */
  }

  .swagger-ui .info hgroup.main {
    margin-bottom: 1.5rem;
  }

  .swagger-ui .info hgroup.main .title {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 0.2em;
    color: #ffffff;
  }

  .swagger-ui .info .description,
  .swagger-ui .info .description p {
    font-size: 1rem;
    color: #d1d5db; /* gray-300 */
  }

  .swagger-ui .opblock-tag-section {
    margin: 2rem 0;
    border-top: 1px solid #374151; /* gray-700 */
    border-bottom: 1px solid #374151; /* gray-700 */
    padding: 1rem 0;
  }

  /* Tag heading (e.g. "documents", "knowledge-base") */
  .swagger-ui .opblock-tag {
    display: inline-block;
    font-size: 1.3rem;
    font-weight: bold;
    padding: 0.5rem 1rem;
    background-color: ${PRIMARY_COLOR};
    color: #fff;
    border-radius: 4px;
    margin-bottom: 1rem;
  }

  /* Expand/Collapse arrow for tag sections */
  .swagger-ui .opblock-tag button.expand-operation {
    background: none;
    border: none;
    cursor: pointer;
    color: #fff;
    margin-left: 0.5rem;
  }

  /* Individual operation blocks (GET, POST, etc.) */
  .swagger-ui .opblock {
    background-color: #1f2937; /* gray-800 */
    border-radius: 8px;
    padding: 1rem 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    border-left: 5px solid #374151; /* gray-700 */
  }

  /* Summary bar at top of each operation */
  .swagger-ui .opblock-summary {
    background: #374151; /* gray-700 */
    padding: 8px 16px;
    border-radius: 6px;
    margin: -1rem -1.5rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  /* Path and method label */
  .swagger-ui .opblock-summary-path {
    font-size: 1rem;
    word-break: break-all; /* so long paths wrap nicely */
    color: #e5e7eb; /* gray-200 */
  }
  .swagger-ui .opblock-summary-method {
    font-weight: bold;
    padding: 0.25rem 0.75rem;
    border-radius: 4px;
    color: #fff;
    margin-right: 0.5rem;
    text-transform: uppercase;
  }
  .swagger-ui .opblock-summary-description {
    font-size: 0.9rem;
    color: #9ca3af; /* gray-400 */
  }

  /* Method colors */
  .swagger-ui .opblock-get .opblock-summary-method {
    background-color: ${SUCCESS_COLOR} !important;
  }
  .swagger-ui .opblock-post .opblock-summary-method {
    background-color: ${PRIMARY_COLOR} !important;
  }
  .swagger-ui .opblock-put .opblock-summary-method {
    background-color: ${WARNING_COLOR} !important;
    color: #000;
  }
  .swagger-ui .opblock-delete .opblock-summary-method {
    background-color: ${DANGER_COLOR} !important;
  }

  /* Try it out / Execute buttons */
  .swagger-ui .opblock .opblock-summary-control,
  .swagger-ui .btn.try-out__btn,
  .swagger-ui .btn.execute,
  .swagger-ui .btn.cancel {
    background-color: ${PRIMARY_COLOR};
    color: #fff;
    font-weight: 600;
    border: none;
    border-radius: 4px;
    padding: 6px 10px;
    margin: 0 4px;
    cursor: pointer;
  }
  .swagger-ui .opblock .opblock-summary-control:hover,
  .swagger-ui .btn.try-out__btn:hover,
  .swagger-ui .btn.execute:hover,
  .swagger-ui .btn.cancel:hover {
    background-color: #2563eb; /* blue-600 */
  }

  /* Authorization button */
  .swagger-ui .btn.authorize {
    background-color: #374151; /* gray-700 */
    color: #e5e7eb; /* gray-200 */
    font-weight: 600;
    border: 1px solid #4b5563; /* gray-600 */
    border-radius: 4px;
    margin-right: 1rem;
  }
  .swagger-ui .btn.authorize.unlocked {
    color: ${PRIMARY_COLOR}; /* reflect "unlocked" state if you want */
    border-color: ${PRIMARY_COLOR};
  }
  .swagger-ui .btn.authorize:hover {
    background-color: ${PRIMARY_COLOR};
    color: #fff;
  }

  /* Inputs and textareas */
  .swagger-ui input[type="text"],
  .swagger-ui input[type="number"],
  .swagger-ui input[type="file"],
  .swagger-ui textarea {
    background-color: #374151; /* gray-700 */
    border: 1px solid #4b5563; /* gray-600 */
    border-radius: 4px;
    padding: 6px;
    margin: 4px 0;
    font-size: 0.95rem;
    color: #e5e7eb; /* gray-200 */
  }

  .swagger-ui input[type="text"]:focus,
  .swagger-ui input[type="number"]:focus,
  .swagger-ui input[type="file"]:focus,
  .swagger-ui textarea:focus {
    outline: none;
    border-color: ${PRIMARY_COLOR};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  /* The "Copy" button for the path */
  .swagger-ui .copy-to-clipboard {
    background: #4b5563; /* gray-600 */
    border-radius: 4px;
    padding: 4px;
    margin-right: 4px;
    cursor: pointer;
    color: #e5e7eb; /* gray-200 */
  }

  /* Expand arrow for each operation (right side) */
  .swagger-ui .opblock-control-arrow {
    background: none;
    border: none;
    cursor: pointer;
    color: #e5e7eb; /* gray-200 */
  }

  /* Models/Schemas section at the bottom */
  .swagger-ui section.models.is-open h4 {
    font-size: 1.2rem;
    margin: 1.5rem 0;
    font-weight: 600;
    color: #ffffff;
  }
  .swagger-ui section.models.is-open .model-box {
    background-color: #1f2937; /* gray-800 */
    padding: 1rem;
    border: 1px solid #374151; /* gray-700 */
    border-radius: 6px;
    margin-bottom: 1rem;
  }
  .swagger-ui section.models.is-open .model-box .model-title {
    font-weight: 600;
    color: #e5e7eb; /* gray-200 */
  }

  /* Additional dark mode overrides */
  .swagger-ui .scheme-container {
    background-color: #1f2937; /* gray-800 */
    border: 1px solid #374151; /* gray-700 */
    border-radius: 4px;
    padding: 1rem;
  }

  .swagger-ui .scheme-container .schemes-title {
    color: #e5e7eb; /* gray-200 */
  }

  .swagger-ui .response-col_status {
    color: #e5e7eb; /* gray-200 */
  }

  .swagger-ui .response-col_description {
    color: #9ca3af; /* gray-400 */
  }

  .swagger-ui .response-col_links {
    color: #9ca3af; /* gray-400 */
  }

  .swagger-ui .responses-table {
    background-color: #1f2937; /* gray-800 */
    border: 1px solid #374151; /* gray-700 */
  }

  .swagger-ui .responses-table th {
    background-color: #374151; /* gray-700 */
    color: #e5e7eb; /* gray-200 */
  }

  .swagger-ui .responses-table td {
    color: #e5e7eb; /* gray-200 */
    border-color: #374151; /* gray-700 */
  }

  .swagger-ui .parameter__name {
    color: #e5e7eb; /* gray-200 */
  }

  .swagger-ui .parameter__type {
    color: #9ca3af; /* gray-400 */
  }

  .swagger-ui .parameter__deprecated {
    color: #fca5a5; /* red-300 */
  }

  .swagger-ui .parameter__required {
    color: #fca5a5; /* red-300 */
  }

  .swagger-ui .parameter__in {
    color: #9ca3af; /* gray-400 */
  }

  .swagger-ui .parameter__description {
    color: #d1d5db; /* gray-300 */
  }

  .swagger-ui .parameters-container {
    background-color: #1f2937; /* gray-800 */
    border: 1px solid #374151; /* gray-700 */
    border-radius: 4px;
    padding: 1rem;
  }

  .swagger-ui .parameters-container th {
    background-color: #374151; /* gray-700 */
    color: #e5e7eb; /* gray-200 */
  }

  .swagger-ui .parameters-container td {
    color: #e5e7eb; /* gray-200 */
    border-color: #374151; /* gray-700 */
  }

  /* Code blocks and examples */
  .swagger-ui .highlight-code {
    background-color: #111827; /* gray-900 */
    border: 1px solid #374151; /* gray-700 */
    border-radius: 4px;
  }

  .swagger-ui .microlight {
    background-color: #111827; /* gray-900 */
    color: #e5e7eb; /* gray-200 */
  }

  /* Headers and labels */
  .swagger-ui .opblock-description-wrapper {
    color: #d1d5db; /* gray-300 */
  }

  .swagger-ui .opblock-description {
    color: #d1d5db; /* gray-300 */
  }

  .swagger-ui .opblock-description p {
    color: #d1d5db; /* gray-300 */
  }

  /* Response examples */
  .swagger-ui .responses-wrapper {
    background-color: #1f2937; /* gray-800 */
    border: 1px solid #374151; /* gray-700 */
    border-radius: 4px;
  }

  .swagger-ui .responses-inner {
    background-color: #1f2937; /* gray-800 */
  }

  /* Schema section */
  .swagger-ui .model {
    background-color: #1f2937; /* gray-800 */
    border: 1px solid #374151; /* gray-700 */
  }

  .swagger-ui .model-title {
    color: #e5e7eb; /* gray-200 */
  }

  .swagger-ui .model-box {
    background-color: #1f2937; /* gray-800 */
    border: 1px solid #374151; /* gray-700 */
  }

  /* Links */
  .swagger-ui a {
    color: ${PRIMARY_COLOR};
  }

  .swagger-ui a:hover {
    color: #2563eb; /* blue-600 */
  }

  /* Fix accordion headers and expandable sections */
  .swagger-ui .opblock-summary-operation-id,
  .swagger-ui .opblock-summary-path__deprecated {
    color: #e5e7eb !important; /* gray-200 */
  }

  .swagger-ui .opblock-summary-description {
    color: #d1d5db !important; /* gray-300 */
  }

  /* Fix all text elements that might be hard to read */
  .swagger-ui h1,
  .swagger-ui h2,
  .swagger-ui h3,
  .swagger-ui h4,
  .swagger-ui h5,
  .swagger-ui h6 {
    color: #ffffff !important;
  }

  .swagger-ui .opblock-summary-path {
    color: #e5e7eb !important; /* gray-200 */
  }

  .swagger-ui .opblock-summary-method {
    color: #ffffff !important;
  }

  /* Fix any remaining text elements */
  .swagger-ui .opblock-summary-control {
    color: #ffffff !important;
  }

  .swagger-ui .opblock-summary-control:hover {
    color: #ffffff !important;
  }

  /* Fix table headers and content */
  .swagger-ui table {
    color: #e5e7eb !important; /* gray-200 */
  }

  .swagger-ui table th {
    color: #ffffff !important;
  }

  .swagger-ui table td {
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix any labels or small text */
  .swagger-ui label,
  .swagger-ui .label {
    color: #d1d5db !important; /* gray-300 */
  }

  /* Fix any remaining text that might be hard to read */
  .swagger-ui .opblock-summary-description {
    color: #d1d5db !important; /* gray-300 */
  }

  .swagger-ui .opblock-summary-path__deprecated {
    color: #fca5a5 !important; /* red-300 */
  }

  /* Fix any code or pre elements */
  .swagger-ui code,
  .swagger-ui pre {
    color: #e5e7eb !important; /* gray-200 */
    background-color: #111827 !important; /* gray-900 */
  }

  /* Fix any remaining text in operation blocks */
  .swagger-ui .opblock .opblock-summary-description {
    color: #d1d5db !important; /* gray-300 */
  }

  /* Fix any text in the info section */
  .swagger-ui .info .title {
    color: #ffffff !important;
  }

  .swagger-ui .info .description {
    color: #d1d5db !important; /* gray-300 */
  }

  /* Fix any text in the models section */
  .swagger-ui .model-box .model-title {
    color: #ffffff !important;
  }

  .swagger-ui .model-box .model-title span {
    color: #ffffff !important;
  }

  /* Fix any remaining text elements */
  .swagger-ui * {
    color: inherit;
  }

  /* Ensure proper contrast for all text */
  .swagger-ui .opblock-summary-path,
  .swagger-ui .opblock-summary-description,
  .swagger-ui .opblock-summary-operation-id {
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix light gray background sections that are hard to read */
  .swagger-ui .opblock-description-wrapper,
  .swagger-ui .opblock-description,
  .swagger-ui .opblock-description p,
  .swagger-ui .opblock-description-wrapper p {
    background-color: #1f2937 !important; /* gray-800 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix parameters section */
  .swagger-ui .parameters-container,
  .swagger-ui .parameters-container th,
  .swagger-ui .parameters-container td {
    background-color: #1f2937 !important; /* gray-800 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix request body section */
  .swagger-ui .body-param,
  .swagger-ui .body-param__text,
  .swagger-ui .body-param__text p {
    background-color: #1f2937 !important; /* gray-800 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix any light gray backgrounds */
  .swagger-ui .opblock-description-wrapper,
  .swagger-ui .opblock-description,
  .swagger-ui .opblock-description p,
  .swagger-ui .opblock-description-wrapper p,
  .swagger-ui .body-param,
  .swagger-ui .body-param__text,
  .swagger-ui .body-param__text p,
  .swagger-ui .parameters-container,
  .swagger-ui .parameters-container th,
  .swagger-ui .parameters-container td {
    background-color: #1f2937 !important; /* gray-800 */
  }

  /* Fix dropdown and select elements */
  .swagger-ui select,
  .swagger-ui .body-param__text select,
  .swagger-ui .parameters-container select {
    background-color: #374151 !important; /* gray-700 */
    color: #e5e7eb !important; /* gray-200 */
    border: 1px solid #4b5563 !important; /* gray-600 */
  }

  /* Fix any remaining light backgrounds */
  .swagger-ui .opblock-description-wrapper,
  .swagger-ui .opblock-description,
  .swagger-ui .opblock-description p,
  .swagger-ui .opblock-description-wrapper p,
  .swagger-ui .body-param,
  .swagger-ui .body-param__text,
  .swagger-ui .body-param__text p,
  .swagger-ui .parameters-container,
  .swagger-ui .parameters-container th,
  .swagger-ui .parameters-container td,
  .swagger-ui .opblock-description-wrapper *,
  .swagger-ui .opblock-description *,
  .swagger-ui .body-param *,
  .swagger-ui .parameters-container * {
    background-color: #1f2937 !important; /* gray-800 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Force dark backgrounds for all content sections */
  .swagger-ui .opblock-description-wrapper,
  .swagger-ui .opblock-description,
  .swagger-ui .opblock-description p,
  .swagger-ui .opblock-description-wrapper p,
  .swagger-ui .body-param,
  .swagger-ui .body-param__text,
  .swagger-ui .body-param__text p,
  .swagger-ui .parameters-container,
  .swagger-ui .parameters-container th,
  .swagger-ui .parameters-container td,
  .swagger-ui .opblock-description-wrapper *,
  .swagger-ui .opblock-description *,
  .swagger-ui .body-param *,
  .swagger-ui .parameters-container *,
  .swagger-ui .opblock-description-wrapper *,
  .swagger-ui .opblock-description *,
  .swagger-ui .body-param *,
  .swagger-ui .parameters-container * {
    background-color: #1f2937 !important; /* gray-800 */
  }

  /* Fix any remaining text color issues */
  .swagger-ui .opblock-description-wrapper *,
  .swagger-ui .opblock-description *,
  .swagger-ui .body-param *,
  .swagger-ui .parameters-container * {
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix specific problematic elements */
  .swagger-ui .body-param__text,
  .swagger-ui .body-param__text p,
  .swagger-ui .body-param__text span,
  .swagger-ui .body-param__text div {
    background-color: #1f2937 !important; /* gray-800 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix any remaining light backgrounds with more specific selectors */
  .swagger-ui .opblock-description-wrapper,
  .swagger-ui .opblock-description,
  .swagger-ui .opblock-description p,
  .swagger-ui .opblock-description-wrapper p,
  .swagger-ui .body-param,
  .swagger-ui .body-param__text,
  .swagger-ui .body-param__text p,
  .swagger-ui .parameters-container,
  .swagger-ui .parameters-container th,
  .swagger-ui .parameters-container td,
  .swagger-ui .opblock-description-wrapper *,
  .swagger-ui .opblock-description *,
  .swagger-ui .body-param *,
  .swagger-ui .parameters-container *,
  .swagger-ui .opblock-description-wrapper *,
  .swagger-ui .opblock-description *,
  .swagger-ui .body-param *,
  .swagger-ui .parameters-container *,
  .swagger-ui .opblock-description-wrapper *,
  .swagger-ui .opblock-description *,
  .swagger-ui .body-param *,
  .swagger-ui .parameters-container * {
    background-color: #1f2937 !important; /* gray-800 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix specific problematic sections from the HTML you provided */
  .swagger-ui .opblock-tag-section,
  .swagger-ui .opblock-tag-section *,
  .swagger-ui .operation-tag-content,
  .swagger-ui .operation-tag-content *,
  .swagger-ui .opblock-body,
  .swagger-ui .opblock-body *,
  .swagger-ui .opblock-section,
  .swagger-ui .opblock-section *,
  .swagger-ui .opblock-section-header,
  .swagger-ui .opblock-section-header *,
  .swagger-ui .tab-header,
  .swagger-ui .tab-header *,
  .swagger-ui .tab-item,
  .swagger-ui .tab-item *,
  .swagger-ui .opblock-title,
  .swagger-ui .opblock-title *,
  .swagger-ui .try-out,
  .swagger-ui .try-out *,
  .swagger-ui .opblock-section-request-body,
  .swagger-ui .opblock-section-request-body *,
  .swagger-ui .parameter__name,
  .swagger-ui .parameter__name *,
  .swagger-ui .content-type-wrapper,
  .swagger-ui .content-type-wrapper *,
  .swagger-ui .body-param-content-type,
  .swagger-ui .body-param-content-type *,
  .swagger-ui .execute-wrapper,
  .swagger-ui .execute-wrapper *,
  .swagger-ui .responses-wrapper,
  .swagger-ui .responses-wrapper *,
  .swagger-ui .responses-inner,
  .swagger-ui .responses-inner *,
  .swagger-ui .responses-table,
  .swagger-ui .responses-table *,
  .swagger-ui .responses-header,
  .swagger-ui .responses-header *,
  .swagger-ui .response,
  .swagger-ui .response *,
  .swagger-ui .response-col_description,
  .swagger-ui .response-col_description *,
  .swagger-ui .response-col_description__inner,
  .swagger-ui .response-col_description__inner *,
  .swagger-ui .response-controls,
  .swagger-ui .response-controls *,
  .swagger-ui .response-control-media-type,
  .swagger-ui .response-control-media-type *,
  .swagger-ui .response-control-media-type__title,
  .swagger-ui .response-control-media-type__title *,
  .swagger-ui .response-control-media-type__accept-message,
  .swagger-ui .response-control-media-type__accept-message *,
  .swagger-ui .model-example,
  .swagger-ui .model-example *,
  .swagger-ui .tab,
  .swagger-ui .tab *,
  .swagger-ui .tabitem,
  .swagger-ui .tabitem *,
  .swagger-ui .tablinks,
  .swagger-ui .tablinks *,
  .swagger-ui .highlight-code,
  .swagger-ui .highlight-code *,
  .swagger-ui .example,
  .swagger-ui .example *,
  .swagger-ui .microlight,
  .swagger-ui .microlight * {
    background-color: #1f2937 !important; /* gray-800 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix specific text elements that might still be hard to read */
  .swagger-ui .opblock-tag span,
  .swagger-ui .opblock-summary-path span,
  .swagger-ui .opblock-summary-description,
  .swagger-ui .opblock-title span,
  .swagger-ui .parameter__name.required,
  .swagger-ui .response-col_description,
  .swagger-ui .response-control-media-type__title,
  .swagger-ui .response-control-media-type__accept-message,
  .swagger-ui .tablinks,
  .swagger-ui .example code,
  .swagger-ui .microlight code {
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix any remaining light backgrounds with universal selector */
  .swagger-ui * {
    background-color: #1f2937 !important; /* gray-800 */
  }

  /* But preserve the main container background */
  .swagger-ui {
    background-color: #111827 !important; /* gray-900 */
  }

  /* Fix any remaining text color issues with universal selector */
  .swagger-ui * {
    color: #e5e7eb !important; /* gray-200 */
  }

  /* But preserve specific colors for important elements */
  .swagger-ui .opblock-summary-method {
    color: #ffffff !important;
  }

  .swagger-ui .opblock-tag {
    color: #ffffff !important;
  }

  .swagger-ui h1,
  .swagger-ui h2,
  .swagger-ui h3,
  .swagger-ui h4,
  .swagger-ui h5,
  .swagger-ui h6 {
    color: #ffffff !important;
  }

  /* Fix SVG icons and buttons to be visible */
  .swagger-ui svg,
  .swagger-ui svg *,
  .swagger-ui svg path,
  .swagger-ui svg g,
  .swagger-ui .arrow,
  .swagger-ui .arrow path,
  .swagger-ui .locked,
  .swagger-ui .locked path,
  .swagger-ui .expand-operation svg,
  .swagger-ui .expand-operation svg path,
  .swagger-ui .opblock-control-arrow svg,
  .swagger-ui .opblock-control-arrow svg path,
  .swagger-ui .copy-to-clipboard svg,
  .swagger-ui .copy-to-clipboard svg path,
  .swagger-ui .authorization__btn svg,
  .swagger-ui .authorization__btn svg path {
    fill: #e5e7eb !important; /* gray-200 */
    color: #e5e7eb !important; /* gray-200 */
  }

  /* Fix button colors and hover states */
  .swagger-ui .btn,
  .swagger-ui .btn *,
  .swagger-ui .btn svg,
  .swagger-ui .btn svg path {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .swagger-ui .btn:hover,
  .swagger-ui .btn:hover *,
  .swagger-ui .btn:hover svg,
  .swagger-ui .btn:hover svg path {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  /* Fix specific button types */
  .swagger-ui .try-out__btn,
  .swagger-ui .execute,
  .swagger-ui .cancel {
    background-color: ${PRIMARY_COLOR} !important;
    color: #ffffff !important;
  }

  .swagger-ui .try-out__btn:hover,
  .swagger-ui .execute:hover,
  .swagger-ui .cancel:hover {
    background-color: #2563eb !important; /* blue-600 */
    color: #ffffff !important;
  }

  /* Fix authorization button */
  .swagger-ui .authorization__btn {
    background-color: #374151 !important; /* gray-700 */
    color: #e5e7eb !important; /* gray-200 */
    border: 1px solid #4b5563 !important; /* gray-600 */
  }

  .swagger-ui .authorization__btn:hover {
    background-color: ${PRIMARY_COLOR} !important;
    color: #ffffff !important;
  }

  /* Fix expand/collapse arrows */
  .swagger-ui .expand-operation,
  .swagger-ui .opblock-control-arrow {
    background: none !important;
    border: none !important;
    color: #e5e7eb !important; /* gray-200 */
  }

  .swagger-ui .expand-operation:hover,
  .swagger-ui .opblock-control-arrow:hover {
    color: #ffffff !important;
  }

  /* Fix copy to clipboard button */
  .swagger-ui .copy-to-clipboard {
    background: #4b5563 !important; /* gray-600 */
    color: #e5e7eb !important; /* gray-200 */
  }

  .swagger-ui .copy-to-clipboard:hover {
    background: #6b7280 !important; /* gray-500 */
    color: #ffffff !important;
  }
`;

const ApiDocs = () => {
  return (
    <div className="flex flex-col h-screen pt-16 bg-gray-900">
      <div className="bg-gray-800 border-b border-gray-700 px-8 py-8">
        <h1 className="text-2xl font-semibold text-white">API Documentation</h1>
        <p className="text-gray-400 mt-1">Explore and test the Oasis API endpoints</p>
      </div>
      <ApplicationNavbar />
      <SwaggerContainer>
        <SwaggerUI url="api-docs.yaml" tryItOutEnabled withCredentials />
      </SwaggerContainer>
    </div>
  );
};

export default ApiDocs;
